import React from 'react';
import type { Editor } from 'ckeditor5';
import { observer } from 'mobx-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { CommonModal } from '../../../../../../../global/modal/common/common-modal';
import { FormField } from '../../../../../../../global/form/form-field/form-field';
import { RichTextEditor } from '../../../../../../../global/rich-text-editor';
import DefaultButton from '../../../../../../../global/default-button/default-button';
import { ModuleDropdown } from '../_events-module/module-dropdown';
import { ADD_TEXT, APPLY_TEXT, CANCEL_TEXT, DESCRIPTION as DESCRIPTION_LABEL_TEXT, } from '../../../../../../../../constants/common-labels.constants';
import { EnablementStatus } from '../../../../../../../../constants/common.constants';
import {
    EDIT_MY_EVENTS_MODULE_MODAL_TITLE_TEXT,
    MODULE_ACTIVE_LABEL_TEXT,
    MODULE_DEFAULT_VIEW_COLLAPSED_OPTION_TEXT,
    MODULE_DEFAULT_VIEW_EXPANDED_OPTION_TEXT,
    MODULE_DEFAULT_VIEW_FIELD_LABEL_TEXT,
    MODULE_EVENTS_DISPLAY_REQUIRED_ERROR_TEXT,
    MODULE_LABEL_DISPLAYED_LABEL_TEXT,
    MODULE_LABEL_FIELD_LABEL_TEXT,
    MODULE_LAYOUT_CAROUSEL_OPTION_TEXT,
    MODULE_LAYOUT_FIELD_LABEL_TEXT,
    MODULE_LAYOUT_LIST_OPTION_TEXT,
    MODULE_PAST_EVENTS_DAYS_PREFIX_TEXT,
    MODULE_PAST_EVENTS_DISPLAYED_LABEL_TEXT,
    MODULE_SPEAKERS_DISPLAYED_LABEL_TEXT,
    MODULE_UPCOMING_EVENTS_DAYS_PREFIX_TEXT,
    MODULE_UPCOMING_EVENTS_DISPLAYED_LABEL_TEXT,
    NEW_MY_EVENTS_MODULE_MODAL_TITLE_TEXT,
} from '../../../../../../event-hubs.text';
import { HubModuleDefaultView, HubModuleLayout } from '../../../../../../../../api/event-hubs.dao';
import { MyEventsModuleVM } from './my-events-module.viewmodel';
import {
    DEFAULT_PAST_EVENTS_DAYS,
    DEFAULT_UPCOMING_EVENTS_DAYS,
    DEFAULT_VIEW,
    DESCRIPTION,
    DISPLAY_LABEL,
    DISPLAY_PAST_EVENTS,
    DISPLAY_SPEAKERS,
    DISPLAY_UPCOMING_EVENTS,
    LABEL,
    LAYOUT,
    PAST_EVENTS_DAYS,
    UPCOMING_EVENTS_DAYS,
} from './my-events-module.config';
import '../_events-module/events-module-modal.scss';
import { FormFieldInput } from '../../../../../../../global/form/form-field-input/form-field-input';
import { EventsRange } from '../_events-module/events-range';

type MyEventsModuleModalProps = {
    vm: MyEventsModuleVM;
    isNew: boolean;
    onCancel: () => void;
    onConfirm: () => void;
};

export const MyEventsModuleModal: React.FC<MyEventsModuleModalProps> = observer(props => {
    const { vm, isNew, onCancel, onConfirm } = props;
    const { formatMessage } = useIntl();

    const labelDisplayedField = vm.getFormVmValuePropsByKey(DISPLAY_LABEL);
    const speakersDisplayedField = vm.getFormVmValuePropsByKey(DISPLAY_SPEAKERS);
    const descriptionField = vm.getFormVmValuePropsByKey(DESCRIPTION);
    const layoutField = vm.getFormVmValuePropsByKey(LAYOUT);
    const defaultViewField = vm.getFormVmValuePropsByKey(DEFAULT_VIEW);
    const upcomingDisplayedField = vm.getFormVmValuePropsByKey(DISPLAY_UPCOMING_EVENTS);
    const upcomingDaysField = vm.getFormVmValuePropsByKey(UPCOMING_EVENTS_DAYS);
    const pastDisplayedField = vm.getFormVmValuePropsByKey(DISPLAY_PAST_EVENTS);
    const pastDaysField = vm.getFormVmValuePropsByKey(PAST_EVENTS_DAYS);

    const titleDescriptor = isNew ? NEW_MY_EVENTS_MODULE_MODAL_TITLE_TEXT : EDIT_MY_EVENTS_MODULE_MODAL_TITLE_TEXT;
    const confirmDescriptor = isNew ? ADD_TEXT : APPLY_TEXT;

    const layoutOptions = [
        { value: HubModuleLayout.CAROUSEL, label: formatMessage(MODULE_LAYOUT_CAROUSEL_OPTION_TEXT) },
        { value: HubModuleLayout.LIST, label: formatMessage(MODULE_LAYOUT_LIST_OPTION_TEXT) },
    ];
    const defaultViewOptions = [
        { value: HubModuleDefaultView.COLLAPSED, label: formatMessage(MODULE_DEFAULT_VIEW_COLLAPSED_OPTION_TEXT) },
        { value: HubModuleDefaultView.EXPANDED, label: formatMessage(MODULE_DEFAULT_VIEW_EXPANDED_OPTION_TEXT) },
    ];

    const handleActiveToggle = () => vm.setEnabled(!vm.isEnabled());
    const handleLabelDisplayedToggle = () => labelDisplayedField.setValue(!labelDisplayedField.value);
    const handleDescriptionChange = (_: unknown, editor: Editor) => descriptionField.setValue(editor.getData());
    const handleSpeakersDisplayedToggle = () => speakersDisplayedField.setValue(!speakersDisplayedField.value);
    const handleLayoutChange = (val: HubModuleLayout) => layoutField.setValue(val);
    const handleDefaultViewChange = (val: HubModuleDefaultView) => defaultViewField.setValue(val);
    const hasDescriptionError = !descriptionField.valid && descriptionField.touched;

    return (
        <CommonModal
            isOpen
            title={<FormattedMessage {...titleDescriptor} />}
            onRequestClose={onCancel}
            className='events-module-modal'
            footer={
                <div className='events-module-modal__footer'>
                    <DefaultButton
                        modifier='cancel'
                        text={<FormattedMessage {...CANCEL_TEXT} />}
                        onClick={onCancel}
                    />
                    <DefaultButton
                        text={<FormattedMessage {...confirmDescriptor} />}
                        onClick={onConfirm}
                    />
                </div>
            }
        >
            <div className='events-module-modal__body'>
                <FormFieldInput.Toggle
                    isSelected={vm.isEnabled()}
                    toggle={handleActiveToggle}
                    text={<FormattedMessage
                        values={{ suffix: vm.isEnabled() ? EnablementStatus.ENABLED : EnablementStatus.DISABLED }}
                        {...MODULE_ACTIVE_LABEL_TEXT}
                    />}
                    className='events-module-modal__toggle-row'
                />

                <div className='events-module-modal__label-row'>
                    <FormFieldInput.Text
                        field={vm.getFormVmValuePropsByKey(LABEL)}
                        labelDescriptor={MODULE_LABEL_FIELD_LABEL_TEXT}
                        required
                        additionalClassName='events-module-modal__label-input'
                        withError
                    />

                    <FormFieldInput.Toggle
                        isSelected={labelDisplayedField.value as boolean}
                        toggle={handleLabelDisplayedToggle}
                        text={<FormattedMessage
                            values={{ suffix: labelDisplayedField.value ? EnablementStatus.DISPLAYED : EnablementStatus.NOT_DISPLAYED }}
                            {...MODULE_LABEL_DISPLAYED_LABEL_TEXT}
                        />}
                        className='events-module-modal__toggle-row events-module-modal__toggle-row--inline'
                    />
                </div>

                <FormField
                    className='m-bottom-20px'
                    label={<FormattedMessage {...DESCRIPTION_LABEL_TEXT} />}
                >
                    <RichTextEditor
                        className='oe-form-field__control m-0px p-0px'
                        value={descriptionField.value as string || ''}
                        onChange={handleDescriptionChange}
                        onBlur={descriptionField.setTouched}
                        validationError={
                            hasDescriptionError &&
                            <FormattedMessage
                                values={{ fieldName: formatMessage(DESCRIPTION_LABEL_TEXT) }}
                                {...descriptionField.validationMessage}
                            />
                        }
                    />
                </FormField>

                <div className='events-module-modal__events-display'>
                    <EventsRange
                        displayedField={upcomingDisplayedField}
                        daysField={upcomingDaysField}
                        displayedLabel={MODULE_UPCOMING_EVENTS_DISPLAYED_LABEL_TEXT}
                        daysPrefixLabel={MODULE_UPCOMING_EVENTS_DAYS_PREFIX_TEXT}
                        defaultDays={DEFAULT_UPCOMING_EVENTS_DAYS}
                    />
                    <EventsRange
                        displayedField={pastDisplayedField}
                        daysField={pastDaysField}
                        displayedLabel={MODULE_PAST_EVENTS_DISPLAYED_LABEL_TEXT}
                        daysPrefixLabel={MODULE_PAST_EVENTS_DAYS_PREFIX_TEXT}
                        defaultDays={DEFAULT_PAST_EVENTS_DAYS}
                    />
                    {!vm.hasValidEventsDisplayConfig() && (
                        <span className='oe-form-field__error'>
                            <FormattedMessage {...MODULE_EVENTS_DISPLAY_REQUIRED_ERROR_TEXT} />
                        </span>
                    )}
                </div>

                <FormFieldInput.Toggle
                    isSelected={speakersDisplayedField.value as boolean}
                    toggle={handleSpeakersDisplayedToggle}
                    text={<FormattedMessage
                        values={{ suffix: speakersDisplayedField.value ? EnablementStatus.DISPLAYED : EnablementStatus.NOT_DISPLAYED }}
                        {...MODULE_SPEAKERS_DISPLAYED_LABEL_TEXT}
                    />}
                    className='events-module-modal__toggle-row'
                />

                <div className='events-module-modal__dropdowns-row m-bottom-20px'>
                    <ModuleDropdown<HubModuleLayout>
                        label={<FormattedMessage {...MODULE_LAYOUT_FIELD_LABEL_TEXT} />}
                        value={layoutField.value as HubModuleLayout}
                        options={layoutOptions}
                        onChange={handleLayoutChange}
                        className='events-module-modal__dropdown-field'
                    />
                    {!vm.isDefaultViewHidden() && (
                        <ModuleDropdown<HubModuleDefaultView>
                            label={<FormattedMessage {...MODULE_DEFAULT_VIEW_FIELD_LABEL_TEXT} />}
                            value={defaultViewField.value as HubModuleDefaultView}
                            options={defaultViewOptions}
                            onChange={handleDefaultViewChange}
                            className='events-module-modal__dropdown-field'
                        />
                    )}
                </div>
            </div>
        </CommonModal>
    );
});
